// Wave 20.49bm — Hand-shape helpers for the gesture tray.
//
// Wave 20.49bt — Added `isThumbsUp` alongside the original
// closed-fist detector. The tray's corner-anchored redesign
// treats a thumbs-up anywhere in frame as an intentional
// "fire" gesture, so the rider never has to aim at a button.

import type { DetectedHand } from './handDetector';

function d3(a: { x: number; y: number; z: number }, b: { x: number; y: number; z: number }): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = a.z - b.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

/**
 * True when the four fingertips (index/middle/ring/pinky) are
 * all curled close to the palm, i.e. a closed fist. Threshold
 * is scale-invariant: fingertip-to-wrist distance is compared
 * against wrist→middle-MCP as the reference length.
 *
 * Landmarks used (MediaPipe hand-landmarker):
 *   0 WRIST · 9 MIDDLE_MCP
 *   8 INDEX_TIP · 12 MIDDLE_TIP · 16 RING_TIP · 20 PINKY_TIP
 */
export function isFistClosed(hand: DetectedHand | null | undefined): boolean {
  if (!hand || !Array.isArray(hand.landmarks) || hand.landmarks.length < 21) {
    return false;
  }
  const lm = hand.landmarks;
  const wrist = lm[0];
  const middleMcp = lm[9];
  const ref = d3(wrist, middleMcp);
  if (!Number.isFinite(ref) || ref <= 0) return false;
  // Sum the four fingertip→wrist distances and divide by 4·ref.
  // Open hand ≈ 3.0; closed fist ≈ 1.2. 1.55 is the mid-cliff.
  const tipSum =
    d3(lm[8], wrist) + d3(lm[12], wrist) + d3(lm[16], wrist) + d3(lm[20], wrist);
  const ratio = tipSum / (4 * ref);
  return ratio < 1.55;
}

/**
 * True when the thumb is extended UP (thumb tip clearly above
 * wrist AND above thumb IP) AND the four other fingers are
 * curled (each fingertip is below its own PIP joint — meaning
 * the finger has folded down toward the palm).
 *
 * "Above" means smaller `y` in normalized image space (0=top,
 * 1=bottom).
 *
 * Deliberately conservative on the thumb-up check but generous
 * on the finger-curl check: an obvious thumbs-up pose fires
 * even if the pinky sticks out a little.
 *
 * Landmarks used:
 *   0  WRIST
 *   3  THUMB_IP     4  THUMB_TIP
 *   5  INDEX_MCP    6  INDEX_PIP    8  INDEX_TIP
 *   9  MIDDLE_MCP  10  MIDDLE_PIP  12  MIDDLE_TIP
 *  13  RING_MCP    14  RING_PIP    16  RING_TIP
 *  17  PINKY_MCP   18  PINKY_PIP   20  PINKY_TIP
 */
export function isThumbsUp(hand: DetectedHand | null | undefined): boolean {
  if (!hand || !Array.isArray(hand.landmarks) || hand.landmarks.length < 21) {
    return false;
  }
  const lm = hand.landmarks;
  const wrist = lm[0];
  const thumbTip = lm[4];
  const thumbIp = lm[3];
  const middleMcp = lm[9];

  // Scale-invariant threshold — the "clearly above" distance is
  // measured relative to wrist→middle-MCP (roughly palm height).
  const refY = Math.abs(wrist.y - middleMcp.y);
  if (!Number.isFinite(refY) || refY <= 0) return false;

  // Thumb must extend UP past the IP joint by a meaningful
  // fraction of the palm height (not just above by a landmark
  // wobble), AND be above the wrist. Both use `<` because
  // smaller y = higher on screen.
  const thumbClearlyUp =
    thumbTip.y < thumbIp.y - refY * 0.35 && thumbTip.y < wrist.y - refY * 0.15;
  if (!thumbClearlyUp) return false;

  // Each of the four other fingers must be curled: fingertip
  // below its PIP joint (closer to the palm). Allow one finger
  // to fail the check (people naturally splay a pinky).
  const curled = [
    lm[8].y > lm[6].y,   // index
    lm[12].y > lm[10].y, // middle
    lm[16].y > lm[14].y, // ring
    lm[20].y > lm[18].y, // pinky
  ];
  const curledCount = curled.filter(Boolean).length;
  return curledCount >= 3;
}
